// src/pages/Admin/Dashboard/AdminDashboard.jsx
import React, { useEffect, useState } from "react";
import { HiUserGroup, HiOfficeBuilding } from "react-icons/hi";
import { FiBriefcase, FiUsers } from "react-icons/fi";

import VisitsChart from "./VisitsChart";
import MessagesSection from "./MessagesSection";
import PageVisitsAnalytics from "./PageVisitsAnalytics";
import NewsletterSubscribersSection from "./NewsletterSubscribersSection";

import { searchAssociations } from "../../../services/associationsServices";
import { searchCommerces } from "../../../services/commercesServices";
import { getAllServicesMunicipaux } from "../../../services/servicesMunicipauxServices";
import { searchTrombinos } from "../../../services/trombinosServices";

const getTotalFromSearch = (res) => {
  if (!res) return 0;
  if (typeof res.total === "number") return res.total;
  if (Array.isArray(res.items)) return res.items.length;
  return 0;
};

export default function AdminDashboard() {
  const [contentStats, setContentStats] = useState({
    associations: 0,
    commerces: 0,
    services: 0,
    trombinos: 0,
  });
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    const fetchContentStats = async () => {
      try {
        setLoadingStats(true);
        const [assocRes, comRes, servicesRes, trombRes] = await Promise.all([
          searchAssociations({ page: 1, limit: 1 }),
          searchCommerces({ page: 1, limit: 1 }),
          getAllServicesMunicipaux(),
          searchTrombinos({ page: 1, limit: 1 }),
        ]);

        const associations = getTotalFromSearch(assocRes);
        const commerces = getTotalFromSearch(comRes);

        const services = Array.isArray(servicesRes)
          ? servicesRes.length
          : getTotalFromSearch(servicesRes);

        const trombinos = getTotalFromSearch(trombRes);

        setContentStats({
          associations,
          commerces,
          services,
          trombinos,
        });
      } catch (e) {
        console.error("Erreur stats contenus (dashboard):", e);
      } finally {
        setLoadingStats(false);
      }
    };

    fetchContentStats();
  }, []);

  return (
    <div className="p-5 md:p-8 space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
            Tableau de bord administratif
          </h1>
          <p className="text-slate-600">
            Vue d’ensemble de l’activité sur le site de la mairie.
          </p>
        </div>
        {/* Petit résumé (optionnel) */}
        <div className="flex flex-wrap gap-3 text-xs md:text-sm">
          <div className="px-3 py-1.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100">
            {loadingStats
              ? "Chargement des contenus…"
              : `${
                  contentStats.associations +
                  contentStats.commerces +
                  contentStats.services +
                  contentStats.trombinos
                } fiches au total`}
          </div>
        </div>
      </div>

      {/* Bloc stats contenus */}
      <section>
        <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
          Contenus du site
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Associations */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase text-slate-500">
                Associations
              </p>
              <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
                {loadingStats ? "…" : contentStats.associations}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Fiches associations référencées.
              </p>
            </div>
            <div className="h-10 w-10 rounded-full bg-sky-50 text-sky-600 flex items-center justify-center">
              <HiUserGroup className="h-5 w-5" />
            </div>
          </div>

          {/* Commerces */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase text-slate-500">
                Commerces & Artisans
              </p>
              <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
                {loadingStats ? "…" : contentStats.commerces}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Commerces présents sur la carte.
              </p>
            </div>
            <div className="h-10 w-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <FiBriefcase className="h-5 w-5" />
            </div>
          </div>

          {/* Services municipaux */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase text-slate-500">
                Services municipaux
              </p>
              <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
                {loadingStats ? "…" : contentStats.services}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Services référencés (piscine, médiathèque…).
              </p>
            </div>
            <div className="h-10 w-10 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center">
              <HiOfficeBuilding className="h-5 w-5" />
            </div>
          </div>

          {/* Trombinoscope */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase text-slate-500">
                Trombinoscope
              </p>
              <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
                {loadingStats ? "…" : contentStats.trombinos}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Profils élus & personnels municipaux.
              </p>
            </div>
            <div className="h-10 w-10 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center">
              <FiUsers className="h-5 w-5" />
            </div>
          </div>
        </div>
      </section>

      {/* Activité : visites + messages */}
      <section className="grid gap-6 lg:grid-cols-[2fr_1.4fr]">
        <VisitsChart />
        <MessagesSection />
      </section>
      {/* Nouvelle analyse des pages */}
      <PageVisitsAnalytics />
      {/* Emails collectés pour informations communales */}
      <NewsletterSubscribersSection />
    </div>
  );
}
